import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import LandingPage from './components/LandingPage';
import MemberLogin from './components/MemberLogin';
import CompanyLogin from './components/CompanyLogin';
import MemberDashboard from './components/MemberDashboard';
import CompanyDashboard from './components/CompanyDashboard';
import PaymentForm from './components/PaymentForm';
import PaymentSuccess from './components/PaymentSuccess';
import './App.css';
import { SpeedInsights } from "@vercel/speed-insights/react";
import axios from 'axios';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Refund from './pages/Refund';
import Contact from './pages/Contact';

// Wrapper for landing page navigation
const LandingPageWrapper = () => {
  const navigate = useNavigate();

  const handleCompanyLogin = () => navigate('/company-login');
  const handleMemberLogin = () => navigate('/member-login');

  return (
    <LandingPage
      onCompanyLogin={handleCompanyLogin}
      onMemberLogin={handleMemberLogin}
    />
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loginType, setLoginType] = useState(null);

  // Restore login state
  useEffect(() => {
    const type = localStorage.getItem('loginType');

    if (type === 'member') {
      const token = localStorage.getItem('memberToken');
      const storedUser = localStorage.getItem('memberData');

      if (token && storedUser) {
        setIsAuthenticated(true);
        setUserData(JSON.parse(storedUser));
        setLoginType('member');
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
    } else if (type === 'company') {
      const token = localStorage.getItem('companyToken');
      const storedUser = localStorage.getItem('companyData');

      if (token && storedUser) {
        setIsAuthenticated(true);
        setUserData(JSON.parse(storedUser));
        setLoginType('company');
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
    }
  }, []);

  // Handle login
  const handleLogin = (data, type) => {
    if (type === 'member') {
      localStorage.setItem('memberToken', data.token);
      localStorage.setItem('memberData', JSON.stringify(data));
    } else if (type === 'company') {
      localStorage.setItem('companyToken', data.token);
      localStorage.setItem('companyData', JSON.stringify(data));
    }

    localStorage.setItem('loginType', type);

    setIsAuthenticated(true);
    setUserData(data);
    setLoginType(type);
    axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.clear(); // or selectively remove keys if needed

    delete axios.defaults.headers.common['Authorization'];

    setIsAuthenticated(false);
    setUserData(null);
    setLoginType(null);
  };

  return (
    <Router>
      <div className="App">
        <SpeedInsights />

        <Routes>
          {/* Landing */}
          <Route path="/" element={<LandingPageWrapper />} />

          {/* Logins */}
          <Route path="/member-login" element={
            isAuthenticated && loginType === 'member'
              ? <Navigate to="/member-dashboard" />
              : <MemberLogin onLogin={handleLogin} />
          } />

          <Route path="/company-login" element={
            isAuthenticated && loginType === 'company'
              ? <Navigate to="/company-dashboard" />
              : <CompanyLogin onLogin={handleLogin} />
          } />

          {/* Dashboards */}
          <Route path="/member-dashboard" element={
            isAuthenticated && loginType === 'member'
              ? <MemberDashboard userData={userData} onLogout={handleLogout} />
              : <Navigate to="/member-login" />
          } />

          <Route path="/company-dashboard" element={
            isAuthenticated && loginType === 'company'
              ? <CompanyDashboard userData={userData} onLogout={handleLogout} />
              : <Navigate to="/company-login" />
          } />

          {/* Payment route for members */}
          <Route path="/payment/:memberId" element={
            isAuthenticated && loginType === 'member'
              ? <PaymentForm userData={userData} />
              : <Navigate to="/member-login" />
          } />

          {/* Other pages */}
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/refund" element={<Refund />} />
          <Route path="/contact" element={<Contact />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
